export const THEME_STORAGE_KEY = "cms_theme";

const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const THEME_CHANGE_EVENT = "cms-theme-change";

const THEME_CHANNEL_NAME = "cms-theme";

export type ThemeMode = "light" | "dark";

export const DEFAULT_THEME: ThemeMode = "light";

function isThemeMode(value: unknown): value is ThemeMode {
  return value === "light" || value === "dark";
}

export function parseThemeMode(value: string | null | undefined): ThemeMode {
  return isThemeMode(value) ? value : DEFAULT_THEME;
}

export function readStoredTheme(): ThemeMode | null {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie.match(
    new RegExp(`(?:^|; )${THEME_STORAGE_KEY}=([^;]*)`),
  );

  if (!match) {
    return null;
  }

  const value = decodeURIComponent(match[1] ?? "");

  return isThemeMode(value) ? value : null;
}

export function getResolvedTheme(): ThemeMode {
  if (typeof document === "undefined") {
    return DEFAULT_THEME;
  }

  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

export function applyTheme(mode: ThemeMode) {
  document.documentElement.classList.toggle("dark", mode === "dark");
}

function writeThemeCookie(mode: ThemeMode) {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${THEME_STORAGE_KEY}=${mode}; path=/; max-age=${THEME_COOKIE_MAX_AGE}; SameSite=Lax`;
}

export function setStoredTheme(mode: ThemeMode) {
  writeThemeCookie(mode);
  applyTheme(mode);
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));

  try {
    const channel = new BroadcastChannel(THEME_CHANNEL_NAME);

    channel.postMessage(mode);
    channel.close();
  } catch {
    // 没有 BroadcastChannel 就不同步：别的标签页下次导航自然会跟上
  }
}

export function subscribeTheme(onChange: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }

  window.addEventListener(THEME_CHANGE_EVENT, onChange);

  let channel: BroadcastChannel | null = null;

  try {
    channel = new BroadcastChannel(THEME_CHANNEL_NAME);
    channel.onmessage = (event) => {
      if (isThemeMode(event.data)) {
        applyTheme(event.data);
      }

      onChange();
    };
  } catch {
    channel = null;
  }

  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, onChange);
    channel?.close();
  };
}
